import { Button } from '@/app/common/components/button/Button'
import { ACETable } from '@/app/common/components/Table/ACETable'
import { AffiliationProvider } from '@/queries/providers/affiliations/schema.ts'
import { createColumnHelper } from '@tanstack/react-table'
import usePermissionAlert from '@/hook/usePermissionAlert'
import { useUserPrivileges } from '@/hook/useUserPrivileges'

type Props = {
  providers: AffiliationProvider[]
  currentPage: number
  recordsPerPage: number
  totalRows: number
  search: string
  onSearchChange: (value: string) => void
  onPageChange: (page: number) => void
  onPerPageChange: (perPage: number) => void
  onRefresh: () => void
  onRemoveProvider: (provider: AffiliationProvider) => void
}

const columnHelper = createColumnHelper<AffiliationProvider>()

type AffiliationProvidersTableColumnActions = {
  canRemoveProvider: boolean
  showPermissionDeniedMessage: () => void | Promise<void>
  onRemoveProvider: (provider: AffiliationProvider) => void
}

const createAffiliationProvidersColumns = ({
  canRemoveProvider,
  showPermissionDeniedMessage,
  onRemoveProvider,
}: AffiliationProvidersTableColumnActions) => [
  columnHelper.accessor('id', {
    header: 'ID',
    cell: (info) => <span>{info.getValue()}</span>,
  }),
  columnHelper.accessor('name', {
    header: 'Name',
    cell: (info) => <span>{info.getValue()}</span>,
  }),
  columnHelper.display({
    id: 'action',
    header: () => <div className="w-full text-center">Action</div>,
    cell: (info) => {
      const row = info.row.original

      return (
        <div className="flex w-full justify-center">
          <Button
            type="button"
            variant="destructive"
            size="xs"
            onClick={() => {
              if (!canRemoveProvider) {
                void showPermissionDeniedMessage()
                return
              }

              onRemoveProvider(row)
            }}
          >
            Remove Provider
          </Button>
        </div>
      )
    },
  }),
]

export default function AffiliationProvidersTable({
  providers,
  currentPage,
  recordsPerPage,
  totalRows,
  search,
  onSearchChange,
  onPageChange,
  onPerPageChange,
  onRefresh,
  onRemoveProvider,
}: Props) {
  const privileges = useUserPrivileges()
  const { showPermissionDeniedMessage } = usePermissionAlert()

  const columns = createAffiliationProvidersColumns({
    canRemoveProvider: privileges.user.provider_management_edit,
    showPermissionDeniedMessage,
    onRemoveProvider,
  })

  return (
    <ACETable<AffiliationProvider>
      data={providers}
      columns={columns}
      totalRows={totalRows}
      currentPage={currentPage}
      recordsPerPage={recordsPerPage}
      search={search}
      onSearchChange={onSearchChange}
      onPageChange={onPageChange}
      onPerPageChange={onPerPageChange}
      onRefresh={onRefresh}
      title="Manage Providers"
      searchPlaceholder="Search Providers"
      filter={<div />}
      emptyMessage="No providers assigned to this affiliation."
    />
  )
}
